import { type ReactNode, useEffect, useState } from 'react'

import { t } from '../../../../../../common/i18n.js'
import type { Language } from '../../../../../../common/types/i18n.js'
import { Button } from '../../../../../components/button/button.js'
import { TextField } from '../../../../../components/forms/text-field/text-field.js'
import { ArrowLeft, Info, X } from '../../../../../components/icon/solar-icons.js'
import { PortalConfirmDialog } from '../../../../../components/portal-confirm-dialog/portal-confirm-dialog.js'
import { Spinner } from '../../../../../components/spinner/spinner.js'
import { EMPLOYEE_FIELD_LABELS, EMPLOYEE_FIELD_SECTIONS } from '../../../utils/field-sections.js'
import {
    addRoleMember,
    createRole as apiCreateRole,
    updateRole as apiUpdateRole,
    fetchLeaveTypes,
    fetchRoleDetail,
    fetchRoleMemberCandidates,
    fetchSettingsCustomFields,
    removeRoleMember,
    type RoleDetail,
    type RoleScopeType,
} from '../../../utils/portal-api.js'
import {
    ADMIN_PERMISSION_KEYS,
    DOCUMENT_TYPES,
    PROFILE_PERMISSION_KEYS,
    WORKSPACE_PERMISSION_KEYS,
} from '../../../utils/rbac-keys.js'
import './role-editor.css'

export interface RoleEditorProps {
    lang: Language
    roleId: number | null
    onClose: () => void
    showToast: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void
}

// ── Sub-components ──

interface PermissionToggleListProps {
    lang: Language
    title: string
    keys: readonly string[]
    selected: Set<string>
    onChange: (next: Set<string>) => void
    labelFor: (key: string) => string
    disabled?: boolean
}

const PermissionToggleList = ({
    lang,
    title,
    keys,
    selected,
    onChange,
    labelFor,
    disabled,
}: PermissionToggleListProps) => {
    const allChecked = keys.every((k) => selected.has(k))

    return (
        <section className="portal-role-editor__section">
            <h3 className="portal-role-editor__section-title">{title}</h3>
            <label className="portal-role-editor__master-toggle">
                <input
                    type="checkbox"
                    checked={allChecked}
                    disabled={disabled}
                    onChange={(e) => {
                        const next = new Set(selected)
                        if (e.target.checked) keys.forEach((k) => next.add(k))
                        else keys.forEach((k) => next.delete(k))
                        onChange(next)
                    }}
                />
                {t.portal.roles.selectAll(lang)}
            </label>
            <div className="portal-role-editor__toggle-grid">
                {keys.map((k) => (
                    <label key={k} className="portal-role-editor__toggle-item">
                        <input
                            type="checkbox"
                            checked={selected.has(k)}
                            disabled={disabled}
                            onChange={(e) => {
                                const next = new Set(selected)
                                if (e.target.checked) next.add(k)
                                else next.delete(k)
                                onChange(next)
                            }}
                        />
                        {labelFor(k)}
                    </label>
                ))}
            </div>
        </section>
    )
}

interface ViewEditPerm {
    view: boolean
    edit: boolean
    // Only meaningful when the grid shows the "Muuda enda" column (profile fields).
    editOwn?: boolean
}

interface ViewEditGridProps {
    lang: Language
    title: string
    rows: Array<{ key: string; label: string }>
    perms: Record<string, ViewEditPerm>
    onChange: (next: Record<string, ViewEditPerm>) => void
    disabled?: boolean
    sections?: Array<{ label: string; fields: string[] }>
    editLabel?: string
    // Adds the third "Muuda enda" column: edit on the holder's OWN profile
    // only, independent of the role's scope.
    showEditOwn?: boolean
}

const ViewEditGrid = ({
    lang,
    title,
    rows,
    perms,
    onChange,
    disabled,
    sections,
    editLabel,
    showEditOwn,
}: ViewEditGridProps) => {
    const allView = rows.every((r) => perms[r.key]?.view)
    const allEdit = rows.every((r) => perms[r.key]?.edit)
    const allEditOwn = rows.every((r) => perms[r.key]?.editOwn)

    const toggleAll = (patch: Partial<ViewEditPerm>) => {
        const next = { ...perms }
        rows.forEach((r) => {
            next[r.key] = { ...(next[r.key] ?? { view: false, edit: false }), ...patch }
        })
        onChange(next)
    }

    const renderCheckbox = (
        key: string,
        isMaster: boolean,
        allChecked: boolean,
        fp: ViewEditPerm,
        prop: keyof ViewEditPerm,
    ) => (
        <td>
            <input
                type="checkbox"
                checked={isMaster ? allChecked : !!fp[prop]}
                disabled={disabled}
                onChange={(e) => {
                    if (isMaster) {
                        toggleAll({ [prop]: e.target.checked })
                    } else {
                        onChange({
                            ...perms,
                            [key]: { ...fp, [prop]: e.target.checked },
                        })
                    }
                }}
            />
        </td>
    )

    const renderRow = (key: string, label: string, isMaster = false) => {
        const fp = perms[key] ?? { view: false, edit: false }
        return (
            <tr
                key={key}
                className={isMaster ? 'portal-field-grid__master' : 'portal-field-grid__field'}
            >
                <td>{label}</td>
                {renderCheckbox(key, isMaster, allView, fp, 'view')}
                {renderCheckbox(key, isMaster, allEdit, fp, 'edit')}
                {showEditOwn && renderCheckbox(key, isMaster, allEditOwn, fp, 'editOwn')}
            </tr>
        )
    }

    return (
        <section className="portal-role-editor__section">
            <h3 className="portal-role-editor__section-title">{title}</h3>
            <div className="portal-field-grid__wrap">
                <table className="portal-field-grid" role="grid">
                    <thead>
                        <tr>
                            <th />
                            <th>{t.portal.roles.colView(lang)}</th>
                            <th>{editLabel ?? t.portal.roles.colEdit(lang)}</th>
                            {showEditOwn && <th>{t.portal.roles.colEditOwn(lang)}</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {renderRow('__all__', t.portal.roles.selectAll(lang), true)}
                        {sections
                            ? sections.map((s) => {
                                  const sectionPerms = s.fields.map(
                                      (f) => perms[f] ?? { view: false, edit: false },
                                  )
                                  const toggleSection = (patch: Partial<ViewEditPerm>) => {
                                      const next = { ...perms }
                                      s.fields.forEach((f) => {
                                          next[f] = {
                                              ...(next[f] ?? { view: false, edit: false }),
                                              ...patch,
                                          }
                                      })
                                      onChange(next)
                                  }
                                  const sectionCheckbox = (
                                      checked: boolean,
                                      prop: keyof ViewEditPerm,
                                  ) => (
                                      <td>
                                          <input
                                              type="checkbox"
                                              checked={checked}
                                              disabled={disabled}
                                              onChange={(e) =>
                                                  toggleSection({ [prop]: e.target.checked })
                                              }
                                          />
                                      </td>
                                  )
                                  return (
                                      <>
                                          <tr key={s.label} className="portal-field-grid__section">
                                              <td>{s.label}</td>
                                              {sectionCheckbox(
                                                  sectionPerms.every((p) => p.view),
                                                  'view',
                                              )}
                                              {sectionCheckbox(
                                                  sectionPerms.every((p) => p.edit),
                                                  'edit',
                                              )}
                                              {showEditOwn &&
                                                  sectionCheckbox(
                                                      sectionPerms.every((p) => p.editOwn),
                                                      'editOwn',
                                                  )}
                                          </tr>
                                          {s.fields.map((f) => {
                                              const label =
                                                  rows.find((r) => r.key === f)?.label ?? f
                                              return renderRow(f, label)
                                          })}
                                      </>
                                  )
                              })
                            : rows.map((r) => renderRow(r.key, r.label))}
                    </tbody>
                </table>
            </div>
        </section>
    )
}

// ── Main component ──

export const RoleEditor = ({ lang, roleId, onClose, showToast }: RoleEditorProps) => {
    const [detail, setDetail] = useState<RoleDetail | null>(null)
    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [scopeType, setScopeType] = useState<RoleScopeType>('everyone')
    const [memberSearch, setMemberSearch] = useState('')
    const [memberResults, setMemberResults] = useState<
        Array<{ user_id: number; first_name: string; last_name: string }>
    >([])
    // Company scope to apply to the next added member (null = all companies).
    const [addCompanyId, setAddCompanyId] = useState<number | null>(null)
    const [saving, setSaving] = useState(false)
    const [dirty, setDirty] = useState(false)
    const [loadingDetail, setLoadingDetail] = useState(false)
    const [showUnsavedDialog, setShowUnsavedDialog] = useState(false)

    // Permission state
    const [workspacePerms, setWorkspacePerms] = useState<Set<string>>(new Set())
    const [profilePerms, setProfilePerms] = useState<Set<string>>(new Set())
    const [adminPerms, setAdminPerms] = useState<Set<string>>(new Set())
    const [fieldPerms, setFieldPerms] = useState<Record<string, ViewEditPerm>>({})
    const [customFieldPerms, setCustomFieldPerms] = useState<
        Record<string, { view: boolean; edit: boolean }>
    >({})
    const [docTypePerms, setDocTypePerms] = useState<
        Record<string, { view: boolean; edit: boolean }>
    >({})
    const [leaveTypePerms, setLeaveTypePerms] = useState<
        Record<string, { view: boolean; edit: boolean }>
    >({})
    const [timeAway, setTimeAway] = useState({ canViewDetails: false, canManage: false })

    // Reference data
    const [customFieldDefs, setCustomFieldDefs] = useState<Array<{ id: number; name: string }>>([])
    const [leaveTypeDefs, setLeaveTypeDefs] = useState<Array<{ id: number; name: string }>>([])

    // Load reference data
    useEffect(() => {
        void (async () => {
            const [cfRes, ltRes] = await Promise.all([
                fetchSettingsCustomFields(),
                fetchLeaveTypes(),
            ])
            if (cfRes.ok) setCustomFieldDefs(cfRes.data.map((f) => ({ id: f.id, name: f.name })))
            if (ltRes.ok) setLeaveTypeDefs(ltRes.data)
        })()
    }, [])

    // Load role detail
    useEffect(() => {
        if (roleId === null) {
            setDetail(null)
            return
        }
        setLoadingDetail(true)
        void (async () => {
            const r = await fetchRoleDetail(roleId)
            if (r.ok) {
                setDetail(r.data)
                setName(r.data.role.name)
                setDescription(r.data.role.description ?? '')
                setScopeType(r.data.role.scope_type)
                // Default the member-scope picker: global admins → "All companies"
                // (null); a company-scoped admin → their first allowed company.
                setAddCompanyId(
                    r.data.companyScope === 'all' ? null : (r.data.companyScope[0] ?? null),
                )
            }
            setLoadingDetail(false)
        })()
    }, [roleId])

    // Hydrate permission state from detail
    useEffect(() => {
        if (!detail?.permissions) return
        const p = detail.permissions
        setWorkspacePerms(new Set(p.workspace))
        setProfilePerms(new Set(p.profile))
        setAdminPerms(new Set(p.admin))

        const fp: typeof fieldPerms = {}
        for (const f of p.fields)
            fp[f.field_key] = { view: f.can_view, edit: f.can_edit, editOwn: f.can_edit_own }
        setFieldPerms(fp)

        const cfp: typeof customFieldPerms = {}
        for (const c of p.customFields)
            cfp[String(c.custom_field_def_id)] = { view: c.can_view, edit: c.can_edit }
        setCustomFieldPerms(cfp)

        const dtp: typeof docTypePerms = {}
        for (const d of p.docTypes) dtp[d.doc_type] = { view: d.can_view, edit: d.can_edit }
        setDocTypePerms(dtp)

        const ltp: typeof leaveTypePerms = {}
        for (const l of p.leaveTypes)
            ltp[String(l.leave_type_id)] = { view: l.can_view, edit: l.can_manage }
        setLeaveTypePerms(ltp)

        setTimeAway(p.timeAway)
    }, [detail])

    // Unsaved changes warning
    useEffect(() => {
        if (!dirty) return
        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault()
        }
        window.addEventListener('beforeunload', handler)
        return () => window.removeEventListener('beforeunload', handler)
    }, [dirty])

    const isSystem = detail?.role.is_system ?? false
    const isAdminLocked = isSystem && detail?.role.name === 'Admin'
    const tR = t.portal.roles

    // Company scope for the member picker. A global admin ('all') may grant any
    // company or "All"; a company-scoped admin is limited to their own.
    const companyScope = detail?.companyScope ?? 'all'
    const canPickAll = companyScope === 'all'
    const allowedCompanies =
        companyScope === 'all'
            ? (detail?.companies ?? [])
            : (detail?.companies ?? []).filter((c) => companyScope.includes(c.id))

    const onSave = async () => {
        if (!name.trim()) return
        setSaving(true)
        try {
            if (roleId === null) {
                const r = await apiCreateRole({
                    name,
                    description: description || null,
                    scope_type: scopeType,
                })
                if (r.ok) onClose()
                else showToast(r.error, 'error')
            } else {
                const r = await apiUpdateRole(roleId, {
                    name: isSystem ? undefined : name,
                    description,
                    scope_type: isSystem ? undefined : scopeType,
                    workspace_permissions: Array.from(workspacePerms),
                    profile_permissions: Array.from(profilePerms),
                    admin_permissions: Array.from(adminPerms),
                    field_permissions: Object.entries(fieldPerms).map(([field_key, p]) => ({
                        field_key,
                        can_view: p.view,
                        can_edit: p.edit,
                        can_edit_own: p.editOwn ?? false,
                    })),
                    custom_field_permissions: Object.entries(customFieldPerms).map(([id, p]) => ({
                        custom_field_def_id: Number(id),
                        can_view: p.view,
                        can_edit: p.edit,
                    })),
                    doc_type_permissions: Object.entries(docTypePerms).map(([doc_type, p]) => ({
                        doc_type,
                        can_view: p.view,
                        can_edit: p.edit,
                    })),
                    leave_type_permissions: Object.entries(leaveTypePerms).map(([id, p]) => ({
                        leave_type_id: Number(id),
                        can_view: p.view,
                        can_manage: p.edit,
                    })),
                    time_away: {
                        can_view_details: timeAway.canViewDetails,
                        can_manage: timeAway.canManage,
                    },
                })
                if (r.ok) onClose()
                else showToast(r.error, 'error')
            }
        } finally {
            setSaving(false)
        }
    }

    const reloadDetail = async () => {
        if (!roleId) return
        const fresh = await fetchRoleDetail(roleId)
        if (fresh.ok) setDetail(fresh.data)
    }

    if (loadingDetail) {
        return (
            <div className="portal-role-editor__loading">
                <Spinner />
            </div>
        )
    }

    const fieldRows = EMPLOYEE_FIELD_SECTIONS.flatMap((s) =>
        s.fields.map((f) => ({
            key: f,
            label: EMPLOYEE_FIELD_LABELS[f]?.[lang] ?? f,
        })),
    )

    const fieldSections = EMPLOYEE_FIELD_SECTIONS.map((s) => ({
        label: s.label[lang],
        fields: s.fields,
    }))

    const workspaceLabel = (k: string): string =>
        t.portal.roles.workspacePermLabels[k as keyof typeof t.portal.roles.workspacePermLabels]?.(
            lang,
        ) ?? k

    const profileLabel = (k: string): string =>
        t.portal.roles.profilePermLabels[k as keyof typeof t.portal.roles.profilePermLabels]?.(
            lang,
        ) ?? k

    const adminLabel = (k: string): string =>
        t.portal.roles.adminPermLabels[k as keyof typeof t.portal.roles.adminPermLabels]?.(lang) ??
        k

    return (
        <div className="portal-role-editor">
            {/* Header */}
            <div className="portal-role-editor__header">
                <button
                    type="button"
                    onClick={() => {
                        if (dirty) {
                            setShowUnsavedDialog(true)
                            return
                        }
                        onClose()
                    }}
                    className="portal-role-editor__back"
                >
                    <ArrowLeft size={16} /> {t.portal.settings.close(lang)}
                </button>
                <div className="portal-role-editor__header-row">
                    <input
                        type="text"
                        className="portal-role-editor__name-input"
                        value={name}
                        onChange={(e) => {
                            setName(e.target.value)
                            setDirty(true)
                        }}
                        disabled={isAdminLocked || isSystem}
                        placeholder={tR.namePlaceholder(lang)}
                    />
                    <span
                        className={`portal-settings__type-badge portal-settings__type-badge--${isSystem ? 'static' : 'dynamic'}`}
                    >
                        {isSystem ? tR.typeSystem(lang) : tR.typeCustom(lang)}
                    </span>
                </div>
            </div>

            <TextField
                as="textarea"
                value={description}
                onChange={(value) => {
                    setDescription(value)
                    setDirty(true)
                }}
                isDisabled={isAdminLocked}
                placeholder={tR.descriptionPlaceholder(lang)}
                textAreaProps={{ rows: 2 }}
            />

            {/* Members section */}
            {roleId !== null && (
                <section className="portal-role-editor__section">
                    <h3 className="portal-role-editor__section-title">{tR.membersSection(lang)}</h3>
                    {detail?.role.is_system && detail.role.name === 'Manager' && (
                        <InfoBanner>{tR.managerAutoBanner(detail.members.length)(lang)}</InfoBanner>
                    )}
                    {detail?.role.is_system && detail.role.name === 'User' && (
                        <InfoBanner>{tR.userAutoBanner(lang)}</InfoBanner>
                    )}
                    <div className="portal-role-editor__members">
                        {detail?.members.map((m) => (
                            <div key={m.user_id} className="portal-role-editor__member-chip">
                                <span>
                                    {m.first_name} {m.last_name}
                                </span>
                                <span className="portal-role-editor__member-company">
                                    {m.company_name ?? tR.companyAll(lang)}
                                </span>
                                {!(detail.role.is_system && detail.role.name === 'User') && (
                                    <button
                                        type="button"
                                        className="portal-role-editor__chip-remove"
                                        onClick={async () => {
                                            const r = await removeRoleMember(roleId, m.user_id)
                                            if (r.ok) void reloadDetail()
                                            else showToast(r.error, 'error')
                                        }}
                                    >
                                        <X size={12} />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                    {/* Add member */}
                    <div className="portal-settings__member-search">
                        {(canPickAll || allowedCompanies.length > 1) && (
                            <select
                                className="portal-role-editor__company-select"
                                aria-label={tR.companyScopeLabel(lang)}
                                value={addCompanyId === null ? '' : String(addCompanyId)}
                                onChange={(e) =>
                                    setAddCompanyId(
                                        e.target.value === '' ? null : Number(e.target.value),
                                    )
                                }
                            >
                                {canPickAll && <option value="">{tR.companyAll(lang)}</option>}
                                {allowedCompanies.map((c) => (
                                    <option key={c.id} value={String(c.id)}>
                                        {c.name}
                                    </option>
                                ))}
                            </select>
                        )}
                        <TextField
                            placeholder={tR.addMemberPlaceholder(lang)}
                            value={memberSearch}
                            onChange={async (value) => {
                                setMemberSearch(value)
                                if (value.length >= 2) {
                                    const r = await fetchRoleMemberCandidates(value)
                                    if (r.ok) setMemberResults(r.data)
                                } else {
                                    setMemberResults([])
                                }
                            }}
                        />
                        {memberResults.length > 0 && (
                            <div className="portal-settings__search-results">
                                {memberResults.map((m) => (
                                    <button
                                        key={m.user_id}
                                        type="button"
                                        className="portal-settings__search-result"
                                        onClick={async () => {
                                            const r = await addRoleMember(
                                                roleId,
                                                m.user_id,
                                                addCompanyId,
                                            )
                                            if (r.ok) {
                                                void reloadDetail()
                                                setMemberSearch('')
                                                setMemberResults([])
                                            } else {
                                                showToast(r.error, 'error')
                                            }
                                        }}
                                    >
                                        {m.first_name} {m.last_name}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* Scope section */}
            <section className="portal-role-editor__section">
                <h3 className="portal-role-editor__section-title">{tR.scopeSection(lang)}</h3>
                <div className="portal-role-editor__scope-options">
                    {(['own', 'reports', 'everyone'] as const).map((s) => (
                        <label key={s} className="portal-role-editor__scope-option">
                            <input
                                type="radio"
                                name="scope"
                                checked={scopeType === s}
                                disabled={isSystem}
                                onChange={() => {
                                    setScopeType(s)
                                    setDirty(true)
                                }}
                            />
                            {tR.scopeOption(s)(lang)}
                        </label>
                    ))}
                </div>
            </section>

            {/* Permission toggle sections — only for existing roles */}
            {roleId !== null && (
                <>
                    <PermissionToggleList
                        lang={lang}
                        title={tR.workspaceSection(lang)}
                        keys={WORKSPACE_PERMISSION_KEYS}
                        selected={workspacePerms}
                        onChange={(next) => {
                            setWorkspacePerms(next)
                            setDirty(true)
                        }}
                        labelFor={workspaceLabel}
                        disabled={isAdminLocked}
                    />
                    <PermissionToggleList
                        lang={lang}
                        title={tR.profileSection(lang)}
                        keys={PROFILE_PERMISSION_KEYS}
                        selected={profilePerms}
                        onChange={(next) => {
                            setProfilePerms(next)
                            setDirty(true)
                        }}
                        labelFor={profileLabel}
                        disabled={isAdminLocked}
                    />
                    <PermissionToggleList
                        lang={lang}
                        title={tR.adminSection(lang)}
                        keys={ADMIN_PERMISSION_KEYS}
                        selected={adminPerms}
                        onChange={(next) => {
                            setAdminPerms(next)
                            setDirty(true)
                        }}
                        labelFor={adminLabel}
                        disabled={isAdminLocked}
                    />

                    {/* Field permissions grid */}
                    <ViewEditGrid
                        lang={lang}
                        title={tR.fieldsSection(lang)}
                        rows={fieldRows}
                        perms={fieldPerms}
                        onChange={(next) => {
                            setFieldPerms(next)
                            setDirty(true)
                        }}
                        disabled={isAdminLocked}
                        sections={fieldSections}
                        showEditOwn
                    />

                    {/* Custom fields */}
                    {customFieldDefs.length > 0 && (
                        <ViewEditGrid
                            lang={lang}
                            title={tR.customFieldsSection(lang)}
                            rows={customFieldDefs.map((f) => ({
                                key: String(f.id),
                                label: f.name,
                            }))}
                            perms={customFieldPerms}
                            onChange={(next) => {
                                setCustomFieldPerms(next)
                                setDirty(true)
                            }}
                            disabled={isAdminLocked}
                        />
                    )}

                    {/* Document types */}
                    <ViewEditGrid
                        lang={lang}
                        title={tR.docTypesSection(lang)}
                        rows={DOCUMENT_TYPES.map((d) => ({
                            key: d,
                            label: tR.docTypeLabels[d]?.(lang) ?? d,
                        }))}
                        perms={docTypePerms}
                        onChange={(next) => {
                            setDocTypePerms(next)
                            setDirty(true)
                        }}
                        disabled={isAdminLocked}
                    />

                    {/* Leave types */}
                    {leaveTypeDefs.length > 0 && (
                        <ViewEditGrid
                            lang={lang}
                            title={tR.leaveTypesSection(lang)}
                            rows={leaveTypeDefs.map((l) => ({
                                key: String(l.id),
                                label: l.name,
                            }))}
                            perms={leaveTypePerms}
                            onChange={(next) => {
                                setLeaveTypePerms(next)
                                setDirty(true)
                            }}
                            disabled={isAdminLocked}
                            editLabel={tR.colManage(lang)}
                        />
                    )}

                    {/* Time away */}
                    <section className="portal-role-editor__section">
                        <h3 className="portal-role-editor__section-title">
                            {tR.timeAwaySection(lang)}
                        </h3>
                        <label className="portal-role-editor__toggle-item">
                            <input
                                type="checkbox"
                                checked={timeAway.canViewDetails}
                                disabled={isAdminLocked}
                                onChange={(e) => {
                                    setTimeAway({
                                        ...timeAway,
                                        canViewDetails: e.target.checked,
                                    })
                                    setDirty(true)
                                }}
                            />
                            {tR.timeAwayView(lang)}
                        </label>
                        <label className="portal-role-editor__toggle-item">
                            <input
                                type="checkbox"
                                checked={timeAway.canManage}
                                disabled={isAdminLocked}
                                onChange={(e) => {
                                    setTimeAway({
                                        ...timeAway,
                                        canManage: e.target.checked,
                                    })
                                    setDirty(true)
                                }}
                            />
                            {tR.timeAwayManage(lang)}
                        </label>
                    </section>
                </>
            )}

            {/* Sticky save bar */}
            <div className="portal-role-editor__sticky-bar">
                <Button
                    variant="tertiary"
                    size="small"
                    text={t.portal.settings.close(lang)}
                    onClick={() => {
                        if (dirty) {
                            setShowUnsavedDialog(true)
                            return
                        }
                        onClose()
                    }}
                />
                <Button
                    variant="primary"
                    size="small"
                    text={saving ? t.common.saving(lang) : t.common.save(lang)}
                    isDisabled={saving || (!dirty && roleId !== null) || isAdminLocked}
                    onClick={onSave}
                />
            </div>

            <PortalConfirmDialog
                open={showUnsavedDialog}
                title={t.portal.shared.confirmTitle(lang)}
                message={tR.unsavedWarning(lang)}
                confirmLabel={t.portal.shared.confirmDiscard(lang)}
                cancelLabel={t.portal.shared.confirmCancel(lang)}
                variant="primary"
                onConfirm={() => {
                    setShowUnsavedDialog(false)
                    onClose()
                }}
                onCancel={() => setShowUnsavedDialog(false)}
            />
        </div>
    )
}

const InfoBanner = ({ children }: { children: ReactNode }) => (
    <div className="portal-role-editor__info-banner">
        <Info size={14} /> {children}
    </div>
)
